/**
 * Scene dressing — the SHARED derivation of a biome's authored `generation`
 * hooks into concrete world placements (AD-315).
 *
 * The client draws its world dressing from the CLIENT CONTENT PACK, not from
 * the wire: the join-ack already carries every non-content input (zone bounds,
 * the cm-quantised collision the client must not contradict, the marker
 * positions), so the only missing inputs are the authored presentation rules —
 * and those are content, delivered by the P4.9/AD-051 hash-handshake pack.
 * Nothing here goes on the wire; `PROTOCOL_VERSION` is untouched.
 *
 * Two derivations live here, and they live here TOGETHER on purpose:
 *
 * - {@link settlementPiecePlacements} is **THE settlement placement law**. The
 *   server's `buildCollision` (backend/src/world/from-content.ts) projects each
 *   returned placement's `colliderShapes` into the descriptor; the client's
 *   `planScene` draws a kit piece at each returned placement. Visual⇄collider
 *   parity is therefore STRUCTURAL — there is one law and both sides call it —
 *   rather than a discipline two files have to keep independently
 *   (`docs/art/empty-biomes-triage-2026-08-19.md` §6: "never a second law").
 * - {@link scatterInstances} derives visual-only, NON-COLLIDING scatter from the
 *   biome's `scatter[]` rules. It is deterministic by construction: an integer
 *   hash PRNG (imul/xor/shift + a division by 2^32) uses no transcendental
 *   function, so every arithmetic operation is IEEE-754-exact and identical on
 *   every engine — same pack ⇒ byte-identical instances everywhere.
 *
 * Framework-free and side-effect-free (no `three`, no I/O, no globals), so both
 * the node-only server and the browser client import it, and `node --test`
 * exercises it with no renderer.
 */
import { pushOutOfShape } from '../sim/collision.js';
/**
 * Flatten a biome's settlements into world-space piece placements, in authored
 * order (settlement order, then piece order within each settlement).
 *
 * A settlement with no `pieces` contributes NOTHING here: its
 * `footprintRadius` stand-in is a collision-only fallback (WR.2) with no
 * geometry to draw, so it stays where it belongs — in `buildCollision`.
 * Order is part of the contract: the server pushes collision shapes in this
 * order and the existing from-content expectations pin those exact bytes.
 */
export function settlementPiecePlacements(settlements) {
    const out = [];
    for (const settlement of settlements ?? []) {
        const pieces = settlement.pieces;
        if (!pieces || pieces.length === 0)
            continue;
        for (const piece of pieces) {
            out.push({
                settlementId: settlement.id,
                assetPack: settlement.assetPack,
                asset: piece.asset,
                x: settlement.position.x + piece.x,
                y: settlement.position.y + (piece.y ?? 0),
                z: settlement.position.z + piece.z,
                rotation: piece.rotation ?? 0,
            });
        }
    }
    return out;
}
/**
 * How near an authored settlement piece must stand to a shrine for it to BE that shrine's
 * landmark (q-player-names-and-wayfinding). A dressed zone draws its shrines from content —
 * the Reach's wayside shrine, Driftwell's unfinished well — never the WR.2 placeholder cone,
 * which the owner kept meeting in the guest demo ("the blue marker still shows", 2026-10-24).
 */
export const SHRINE_LANDMARK_RADIUS_M = 2;
/** True when an authored settlement piece stands within {@link SHRINE_LANDMARK_RADIUS_M} of (x, z). */
export function hasAuthoredLandmark(placements, x, z) {
    return placements.some((p) => Math.hypot(p.x - x, p.z - z) <= SHRINE_LANDMARK_RADIUS_M);
}
/**
 * Whether a biome is DRESSED: it authors settlements or scatter, so its world is drawn from
 * content. Undressed zones keep the WR.2 placeholder markers as honest scaffolding.
 */
export function isDressedGeneration(generation) {
    return (generation?.settlements?.length ?? 0) > 0 || (generation?.scatter?.length ?? 0) > 0;
}
// ---------------------------------------------------------------------------
// Scatter — deterministic, non-colliding, capped
// ---------------------------------------------------------------------------
/** Square metres of ground that yield ONE instance at `density: 1` (20 m × 20 m). */
export const SCATTER_AREA_UNIT_SQM = 400;
/**
 * Hard per-rule instance ceiling. Density is author-tunable DATA that the
 * content waves tune against the visual-lane bar; this ceiling exists so a
 * mistyped density can never blow the WR.3.4 draw budget. It is scaled by the
 * zone's population weight ({@link scatterCountFor}), never by authored data.
 */
export const MAX_SCATTER_PER_RULE = 128;
/** Instances stay this far inside the zone bounds (world units). */
export const SCATTER_EDGE_INSET = 2;
/** No instance is placed within this radius of a shrine/transition/POI (world units). */
export const MARKER_CLEAR_RADIUS = 4;
/** The clearance circle a candidate instance is tested with against collision. */
export const SCATTER_PROBE_RADIUS = 0.5;
/** Candidate positions tried per instance before that instance is skipped. */
export const SCATTER_PLACEMENT_TRIES = 8;
/** Instance scale jitter, inclusive-ish bounds (uniform in [MIN, MAX)). */
export const SCATTER_SCALE_MIN = 0.85;
export const SCATTER_SCALE_MAX = 1.25;
/**
 * The yards scatter must leave open (sprint-assembly-1, AD-647): every settlement
 * that authors BOTH `pieces` and a `footprintRadius` contributes a circle of that
 * radius at its `position`. Without pieces the radius is the settlement's stand-in
 * COLLIDER (WR.2) and is already honoured through the descriptor collision; with
 * pieces the collision comes from the pieces, and the radius instead marks the open
 * ground between them (a training yard, a market square) that trees and boulders
 * should not grow in. Visual only — nothing server-side reads it. No committed
 * settlement authored both before this, so every existing zone's scatter is unchanged.
 */
export function settlementClearings(settlements) {
    const out = [];
    for (const settlement of settlements ?? []) {
        const radius = settlement.footprintRadius;
        if (!settlement.pieces || settlement.pieces.length === 0)
            continue;
        if (radius === undefined || !(radius > 0))
            continue;
        out.push({ x: settlement.position.x, z: settlement.position.z, radius });
    }
    return out;
}
/** FNV-1a over a string → a 32-bit unsigned integer. Integer-only, no floats. */
function fnv1a(text) {
    let hash = 0x811c9dc5;
    for (let i = 0; i < text.length; i += 1) {
        hash ^= text.charCodeAt(i);
        hash = Math.imul(hash, 0x01000193);
    }
    return hash >>> 0;
}
/**
 * mulberry32 — a 32-bit-integer PRNG. The only floating-point operation is the
 * final division by 2^32, which is exact in IEEE-754 double precision, so the
 * stream is bit-identical on every JavaScript engine.
 */
function mulberry32(seed) {
    let state = seed >>> 0;
    return () => {
        state = (state + 0x6d2b79f5) >>> 0;
        let t = state;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}
// ---------------------------------------------------------------------------
// Population density — the owner's 2026-08-05 directive as a parameter (E3.3.S6)
// ---------------------------------------------------------------------------
/**
 * The highest zone tier a player crosses on foot. ASSUMED: no mount system is
 * built and no design doc names the tier a mount is earned at, so this is the
 * GDD's beginner band (tier 1, levels 1–10) — the one constant to move when a
 * mount lands (`docs/design/world-structure.md` §World Density).
 */
export const PRE_MOUNT_MAX_TIER = 1;
/** The lowest weight any tier may carry: the population never goes to zero after mounts. */
export const POPULATION_DENSITY_FLOOR = 0.5;
/**
 * Presentational population weight per zone tier — a multiplier on every
 * authored scatter rule's instance count AND its ceiling. Weighted toward the
 * pre-mount tier (travel is slowest there), never below
 * {@link POPULATION_DENSITY_FLOOR} afterwards. Tier 0 is the safe towns, whose
 * dressing is an authored layout, so it keeps its authored density.
 *
 * ⛔ Presentation ONLY: this scales visual, non-colliding scatter. Resource
 * nodes, enemy spawns, drops, yields and rewards are Owner-Approval-Domain
 * values (AD-021/024/039/070) and nothing here reads or writes them.
 */
export const POPULATION_DENSITY_BY_TIER = {
    0: 1,
    1: 1.5,
    2: 1,
    3: 1,
};
/** The population weight for a zone tier; an unknown/absent tier ⇒ 1 (authored density). */
export function populationDensityScale(tier) {
    if (tier === undefined || tier === null)
        return 1;
    const weight = POPULATION_DENSITY_BY_TIER[tier];
    return typeof weight === 'number' && weight > 0 ? weight : 1;
}
/**
 * The instance count a rule yields over an area, floored at 1 and capped. The
 * zone's population `scale` multiplies the count and the ceiling together, so
 * a rule already at the ceiling still gains in a weighted tier; the result is
 * bounded by `MAX_SCATTER_PER_RULE × scale` whatever the authored density.
 */
export function scatterCountFor(rule, areaSqm, scale = 1) {
    const density = typeof rule.density === 'number' && rule.density > 0 ? rule.density : 1;
    const weight = scale > 0 ? scale : 1;
    const raw = Math.round((density * areaSqm * weight) / SCATTER_AREA_UNIT_SQM);
    return Math.max(1, Math.min(Math.round(MAX_SCATTER_PER_RULE * weight), raw));
}
/**
 * Derive the visual scatter instances for a zone.
 *
 * Deterministic: the PRNG for rule `i` is seeded from `"<biomeId>#<i>#<seed>"`,
 * candidates are drawn uniformly inside the inset bounds, and a candidate is
 * REJECTED when its clearance circle overlaps any descriptor collision shape
 * (tested with the sim's own `pushOutOfShape`, so scatter clears walls exactly
 * as characters do) or when it lies within {@link MARKER_CLEAR_RADIUS} of a
 * marker. The stream advances identically whether or not a candidate is taken,
 * so every client derives the same list from the same pack.
 *
 * Degenerate inputs degrade rather than throw: no rules, zero-area bounds, or a
 * rule whose pack delivers no assets each contribute nothing.
 */
export function scatterInstances(input) {
    const { bounds } = input;
    const out = [];
    const rules = input.rules ?? [];
    if (rules.length === 0)
        return out;
    const minX = bounds.minX + SCATTER_EDGE_INSET;
    const maxX = bounds.maxX - SCATTER_EDGE_INSET;
    const minZ = bounds.minZ + SCATTER_EDGE_INSET;
    const maxZ = bounds.maxZ - SCATTER_EDGE_INSET;
    const spanX = maxX - minX;
    const spanZ = maxZ - minZ;
    if (spanX <= 0 || spanZ <= 0)
        return out;
    const areaSqm = (bounds.maxX - bounds.minX) * (bounds.maxZ - bounds.minZ);
    const shapes = input.collision ?? [];
    const markers = input.markers ?? [];
    const clearings = input.clearings ?? [];
    const seed = input.seed ?? 0;
    const scale = populationDensityScale(input.tier);
    for (let ruleIndex = 0; ruleIndex < rules.length; ruleIndex += 1) {
        const rule = rules[ruleIndex];
        if (rule === undefined)
            continue;
        const assets = input.packAssets(rule.assetPack);
        if (assets.length === 0)
            continue;
        const rand = mulberry32(fnv1a(`${input.biomeId}#${ruleIndex}#${seed}`));
        const count = scatterCountFor(rule, areaSqm, scale);
        // `exclude` (W42): an excluded draw is dropped AFTER the stream advances, never
        // re-rolled, so the rest of the rule's dressing is byte-identical to before.
        const excluded = new Set(rule.exclude ?? []);
        const clearance = rule.clearance ?? {};
        for (let i = 0; i < count; i += 1) {
            // The asset of draw `i` does not consume the stream, so it is known before the
            // positions are tried: a listed asset is probed at its own crown radius.
            const assetId = assets[fnv1a(`${input.biomeId}#${ruleIndex}#a${i}`) % assets.length];
            const crown = assetId === undefined ? undefined : clearance[assetId];
            const probe = crown === undefined
                ? SCATTER_PROBE_RADIUS
                : Math.max(SCATTER_PROBE_RADIUS, crown * SCATTER_SCALE_MAX);
            let placedX = 0;
            let placedZ = 0;
            let placed = false;
            for (let attempt = 0; attempt < SCATTER_PLACEMENT_TRIES; attempt += 1) {
                const x = minX + rand() * spanX;
                const z = minZ + rand() * spanZ;
                if (placed)
                    continue; // keep the stream identical; stop testing once placed
                if (isClear(x, z, probe, shapes, markers, clearings)) {
                    placedX = x;
                    placedZ = z;
                    placed = true;
                }
            }
            // Drawn unconditionally so a rejected instance does not shift the stream.
            const rotationY = rand() * Math.PI * 2;
            const scale = SCATTER_SCALE_MIN + rand() * (SCATTER_SCALE_MAX - SCATTER_SCALE_MIN);
            if (!placed || assetId === undefined || excluded.has(assetId))
                continue;
            out.push({
                assetPack: rule.assetPack,
                assetId,
                x: placedX,
                y: 0,
                z: placedZ,
                rotationY,
                scale,
            });
        }
    }
    return out;
}
/** True when a candidate clears every collision shape, marker clearing and authored clearing. */
function isClear(x, z, probe, shapes, markers, clearings) {
    for (const shape of shapes) {
        if (pushOutOfShape(x, z, probe, shape) !== null)
            return false;
    }
    for (const marker of markers) {
        if (Math.hypot(x - marker.x, z - marker.z) < MARKER_CLEAR_RADIUS)
            return false;
    }
    for (const clearing of clearings) {
        if (Math.hypot(x - clearing.x, z - clearing.z) < clearing.radius)
            return false;
    }
    return true;
}
// ---------------------------------------------------------------------------
// Client-pack narrowing
// ---------------------------------------------------------------------------
/** A client-pack biome def's zone tier, or null when absent / not a known tier. */
export function clientBiomeTierOf(def) {
    if (def === null || def === undefined)
        return null;
    const tier = def['tier'];
    return tier === 0 || tier === 1 || tier === 2 || tier === 3 ? tier : null;
}
/**
 * Narrow an untyped client-pack biome def's `generation` into the typed client
 * shape, defensively: a pack from an unknown/older build, or one whose fields
 * are the wrong type, yields `null` rather than a cast that lies. Only the two
 * arrays that survive projection are read — an unexpected extra field on a
 * hand-edited pack is simply ignored.
 */
export function clientGenerationOf(def) {
    if (def === null || def === undefined)
        return null;
    const generation = def['generation'];
    if (generation === null || typeof generation !== 'object')
        return null;
    const source = generation;
    const out = {};
    if (Array.isArray(source['scatter']))
        out.scatter = source['scatter'];
    if (Array.isArray(source['settlements'])) {
        out.settlements = source['settlements'];
    }
    if (out.scatter === undefined && out.settlements === undefined)
        return null;
    return out;
}
//# sourceMappingURL=scene-dressing.js.map